import React, { useState, useEffect } from "react";
import { Upload, Trash2, Star, X } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import { useCommercialInteriorDesignCategory } from "../../../Containers/commercial-design/useHook";
import { UrduInput } from "../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../Components/Shared/ImageSeoFields";
import { DESIGN_TYPE_OPTIONS, urduFor } from "../../../Utils/staticDropdownOptions";

const CommercialInteriorDesignModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditing = false,
  editData = null,
  isSubmitting = false
}) => {
  const { getAllCommercialInteriorDesignCategories } = useCommercialInteriorDesignCategory();
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    title_ur: "",
    description: "",
    description_ur: "",
    customUrl: "",
    plot_dimension: "",
    covered_area: "",
    floors: 0,
    bed_rooms: 0,
    bath_rooms: 0,
    category_id: "",
    room_size: "",
    design_type: "",
    design_type_ur: "",
    images: [],
    imagePreviews: [],
    imagePositions: [],
    imageAlts: [],
    imageTitles: [],
    primaryImageIndex: 0,
    features: {
      carParking: false,
      terrace: false,
      servantQuarter: false,
    }
  });

  const [imageError, setImageError] = useState("");
  const [existingImages, setExistingImages] = useState([]);
  const [existingImagePositions, setExistingImagePositions] = useState({});
  const [primaryExistingImageId, setPrimaryExistingImageId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    if (editData && isEditing) {
      setFormData({
        title: editData.title || "",
        title_ur: editData.title_ur || "",
        description: editData.description || "",
        description_ur: editData.description_ur || "",
        customUrl: editData.custom_url || editData.customUrl || "",
        plot_dimension: editData.plot_dimension || "",
        covered_area: editData.covered_area || "",
        floors: editData.floors || 0,
        bed_rooms: editData.bed_rooms || 0,
        bath_rooms: editData.bath_rooms || 0,
        category_id: editData.category_id || editData.category?.id || "",
        room_size: editData.room_size || "",
        design_type: editData.design_type || "",
        design_type_ur: editData.design_type_ur || "",
        images: [],
        imagePreviews: [],
        imagePositions: [],
        imageAlts: [],
        imageTitles: [],
        primaryImageIndex: null,
        features: {
          carParking: editData.car_parking || editData.features?.carParking || false,
          terrace: editData.terrace || editData.features?.terrace || false,
          servantQuarter: editData.servant_quarters || editData.features?.servantQuarter || false,
        }
      });

      const existingImgs = editData.existingImages || editData.images || [];
      setExistingImages(existingImgs);
      const positions = {};
      let primaryId = null;
      existingImgs.forEach((img) => {
        if (img.position) positions[img.id] = img.position;
        if (img.is_primary) primaryId = img.id;
      });
      setExistingImagePositions(positions);
      setPrimaryExistingImageId(primaryId);
    } else if (!isEditing) {
      resetForm();
    }
  }, [editData, isEditing, isOpen]);

  const resetForm = () => {
    if (formData.imagePreviews) {
      formData.imagePreviews.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    }

    setFormData({
      title: "",
      title_ur: "",
      description: "",
      description_ur: "",
      customUrl: "",
      plot_dimension: "",
      covered_area: "",
      floors: 0,
      bed_rooms: 0,
      bath_rooms: 0,
      category_id: "",
      room_size: "",
      design_type: "",
      design_type_ur: "",
      images: [],
      imagePreviews: [],
      imagePositions: [],
      imageAlts: [],
      imageTitles: [],
      primaryImageIndex: 0,
      features: {
        carParking: false,
        terrace: false,
        servantQuarter: false,
      }
    });
    setExistingImages([]);
    setExistingImagePositions({});
    setPrimaryExistingImageId(null);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      const response = await getAllCommercialInteriorDesignCategories();
      setCategories(response);
    };
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const nextValue =
        type === "checkbox"
          ? checked
          : type === "number"
            ? parseInt(value) || 0
            : value;
      const next = { ...prev, [name]: nextValue };
      if (name === "design_type") {
        next.design_type_ur = urduFor(DESIGN_TYPE_OPTIONS, nextValue);
      }
      return next;
    });
  };

  const handleQuillChange = (value) => {
    // Functional update — ReactQuill often fires onChange right after value is set;
    // spreading stale formData was wiping title/customUrl/category/room_size/etc.
    setFormData((prev) => ({
      ...prev,
      description: value,
    }));
  };

  const handleQuillChangeUr = (value) => {
    setFormData((prev) => ({
      ...prev,
      description_ur: value,
    }));
  };

  const handleFeatureChange = (featureName) => {
    setFormData((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [featureName]: !prev.features[featureName],
      },
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newValidImages = [];
    const newImagePreviews = [];

    const checkImageResolution = (file) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width === 1200 && img.height === 900) {
            resolve(true);
          } else {
            resolve(false);
          }
        };
        img.src = URL.createObjectURL(file);
      });
    };

    Promise.all(files.map((file) => checkImageResolution(file))).then(
      (results) => {
        results.forEach((isValid, index) => {
          if (isValid) {
            newValidImages.push(files[index]);
            newImagePreviews.push(URL.createObjectURL(files[index]));
          } else {
            alert(
              `Image "${files[index].name}" does not have the required resolution of 1200*900. Please upload the correct resolution.`
            );
          }
        });
        setFormData((prev) => {
          const currentImageCount = prev.images ? prev.images.length : 0;
          const newPrimaryIndex = currentImageCount === 0 ? 0 : prev.primaryImageIndex;

          return {
            ...prev,
            images: [...(prev.images || []), ...newValidImages],
            imagePreviews: [...(prev.imagePreviews || []), ...newImagePreviews],
            imageAlts: [
              ...(prev.imageAlts || []),
              ...newValidImages.map(() => ""),
            ],
            imageTitles: [
              ...(prev.imageTitles || []),
              ...newValidImages.map(() => ""),
            ],
            imagePositions: [...(prev.imagePositions || []), ...Array(newValidImages.length).fill("")],
            primaryImageIndex: newPrimaryIndex,
          };
        });
      }
    );
  };

  const removeImage = (index, isExisting = false) => {
    if (isExisting) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      const updatedImages = [...formData.images];
      const updatedPreviews = [...(formData.imagePreviews || [])];
      const updatedPositions = [...(formData.imagePositions || [])];
      const updatedAlts = [...(formData.imageAlts || [])];
      const updatedTitles = [...(formData.imageTitles || [])];

      if (updatedPreviews[index]) {
        URL.revokeObjectURL(updatedPreviews[index]);
      }

      updatedImages.splice(index, 1);
      updatedPreviews.splice(index, 1);
      updatedPositions.splice(index, 1);
      updatedAlts.splice(index, 1);
      updatedTitles.splice(index, 1);

      let newPrimaryIndex = formData.primaryImageIndex;
      if (index === formData.primaryImageIndex) {
        newPrimaryIndex = null;
      } else if (index < formData.primaryImageIndex) {
        newPrimaryIndex = formData.primaryImageIndex - 1;
      }

      setFormData({
        ...formData,
        images: updatedImages,
        imagePreviews: updatedPreviews,
        imagePositions: updatedPositions,
        imageAlts: updatedAlts,
        imageTitles: updatedTitles,
        primaryImageIndex: newPrimaryIndex,
      });

      if (updatedImages.length === 0 && existingImages.length === 0) {
        setImageError("At least one image is required");
      }
    }
  };

  const setPrimaryImage = (index) => {
    setFormData({
      ...formData,
      primaryImageIndex: index,
    });
    setPrimaryExistingImageId(null);
  };

  const setPrimaryExistingImage = (id) => {
    setPrimaryExistingImageId(id);
    setFormData({
      ...formData,
      primaryImageIndex: null,
    });
  };

  const handlePositionChange = (index, value) => {
    const updatedPositions = [...(formData.imagePositions || [])];
    updatedPositions[index] = value;
    setFormData({
      ...formData,
      imagePositions: updatedPositions,
    });
  };

  const handleExistingPositionChange = (id, value) => {
    setExistingImagePositions({
      ...existingImagePositions,
      [id]: value
    });
  };

  const handleExistingImageSeoChange = (index, field, value) => {
    setExistingImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, [field]: value } : img)),
    );
  };

  const handleNewImageSeoChange = (index, field, value) => {
    const key = field === "image_alt" ? "imageAlts" : "imageTitles";
    setFormData((prev) => {
      const next = [...(prev[key] || [])];
      next[index] = value;
      return { ...prev, [key]: next };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.customUrl || formData.customUrl.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL is required. Please enter a custom URL.",
      });
      return;
    }

    if (formData.customUrl.trim().startsWith('/')) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL cannot start with a forward slash (/). Please enter a valid URL.",
      });
      return;
    }

    if ((!formData.images || formData.images.length === 0) && (!existingImages || existingImages.length === 0)) {
      setImageError("At least one image is required");
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please upload at least one image before submitting.",
      });
      return;
    }

    const formDataWithExistingImages = {
      ...formData,
      existingImages: existingImages,
      existingImagePositions: existingImagePositions,
      primaryExistingImageId: primaryExistingImageId
    };

    onSubmit(formDataWithExistingImages);

    // Reset form after successful submission (only for new additions, not for editing)
    if (!isEditing) {
      resetForm();
    }
  };

  const handleClose = () => {
    if (formData.imagePreviews) {
      formData.imagePreviews.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    }
    resetForm();
    onClose();
  };

  useEffect(() => {
    return () => {
      if (formData.imagePreviews) {
        formData.imagePreviews.forEach((url) => {
          URL.revokeObjectURL(url);
        });
      }
    };
  }, [formData.imagePreviews]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-3/4 max-w-6xl relative max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Edit Commercial Interior Design" : "Add Commercial Interior Design"}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          {/* Basic Information */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
                <UrduInput
                  label="عنوان"
                  name="title_ur"
                  value={formData.title_ur}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="customUrl"
                  value={formData.customUrl}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  placeholder="Select Category"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Commercial Interior Design Information */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plot Dimension *
                </label>
                <input
                  type="text"
                  name="plot_dimension"
                  value={formData.plot_dimension}
                  onChange={handleChange}
                  placeholder="e.g., 30x50 ft"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div> */}

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Covered Area *
                </label>
                <input
                  type="text"
                  name="covered_area"
                  value={formData.covered_area}
                  onChange={handleChange}
                  placeholder="e.g., 1500 sq ft"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div> */}

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Floors *
                </label>
                <input
                  type="number"
                  name="floors"
                  value={formData.floors}
                  onChange={handleChange}
                  min="0"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bedrooms *
                </label>
                <input
                  type="number"
                  name="bed_rooms"
                  value={formData.bed_rooms}
                  onChange={handleChange}
                  min="0"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bathrooms *
                </label>
                <input
                  type="number"
                  name="bath_rooms"
                  value={formData.bath_rooms}
                  onChange={handleChange}
                  min="0"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Room Size *
                </label>
                <input
                  type="text"
                  name="room_size"
                  value={formData.room_size}
                  onChange={handleChange}
                  placeholder="e.g., 12x15 ft"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Design Type *
                </label>
                <select
                  name="design_type"
                  value={formData.design_type}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                >
                  <option value="">Select design type</option>
                  {DESIGN_TYPE_OPTIONS.map((o) => (
                    <option key={o.en} value={o.en}>
                      {o.en}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ڈیزائن کی قسم
                </label>
                <select
                  name="design_type_ur"
                  value={formData.design_type_ur}
                  onChange={handleChange}
                  dir="rtl"
                  className="w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="">منتخب کریں...</option>
                  {DESIGN_TYPE_OPTIONS.map((o) => (
                    <option key={o.ur} value={o.ur}>
                      {o.ur}
                    </option>
                  ))}
                </select>
              </div>

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Associate to Category *
                </label>
                <select
                  name="associated_house"
                  value={formData.associated_house}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                >
                  <option value="">Select category</option>
                  <option value="3 Marla">3 Marla</option>
                  <option value="5 Marla">5 Marla</option>
                  <option value="8 Marla">8 Marla</option>
                  <option value="10 Marla">10 Marla</option>
                  <option value="1 kanal">1 Kanal</option>
                  <option value="2 kanal">2 Kanal</option>
                  <option value="others">Others</option>
                </select>
              </div> */}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <div className="rounded-md">
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={handleQuillChange}
                className="h-40"
              />
            </div>
          </div>
          <div className="mb-4 pt-14">
            <label className="block text-sm font-medium text-indigo-700 text-right mb-1">
              تفصیل
            </label>
            <div className="quill-ur-interior border border-indigo-200 rounded-md mb-16">
              <style>{`.quill-ur-interior .ql-editor { direction: rtl; text-align: right; }`}</style>
              <ReactQuill
                theme="snow"
                value={formData.description_ur}
                onChange={handleQuillChangeUr}
                className="h-40"
              />
            </div>
          </div>

          {/* Features Checkboxes */}
          {/* <div className="mb-6 mt-14">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Features
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.keys(formData.features).map((feature) => (
                <div
                  key={feature}
                  className="flex items-center space-x-3 bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors duration-200"
                >
                  <input
                    type="checkbox"
                    id={feature}
                    name={feature}
                    checked={formData.features[feature]}
                    onChange={() => handleFeatureChange(feature)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor={feature}
                    className="text-sm font-medium text-gray-700 cursor-pointer select-none capitalize flex-1"
                  >
                    {feature === 'carParking'
                      ? 'Car Parking'
                      : feature === 'servantQuarter'
                        ? 'Servant Quarter'
                        : feature === 'terrace'
                          ? 'Terrace'
                          : feature.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                </div>
              ))}
            </div>
          </div> */}

          {/* Existing Images Display */}
          {isEditing && existingImages.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 mt-6">
                Current Images
              </label>
              <div className="grid grid-cols-4 gap-4">
                {existingImages.map((image, index) => (
                  <div key={image.id || index} className="flex flex-col space-y-2">
                    <div className="relative group">
                      <div className={`h-24 border rounded-md flex items-center justify-center bg-gray-100 overflow-hidden ${primaryExistingImageId === image.id ? 'ring-2 ring-blue-500' : ''}`}>
                        <img
                          src={image.image_url}
                          alt={`Existing image ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      {primaryExistingImageId === image.id && (
                        <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full p-1 z-10">
                          <Star size={12} fill="white" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(index, true)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-sm"
                      >
                        <Trash2 size={12} />
                      </button>
                      {primaryExistingImageId !== image.id && (
                        <button
                          type="button"
                          onClick={() => setPrimaryExistingImage(image.id)}
                          className="absolute bottom-1 left-1 bg-green-500 text-white rounded-full p-1 hover:bg-green-600 transition-colors opacity-0 group-hover:opacity-100 z-10 shadow-sm"
                          title="Set as Primary Image"
                        >
                          <Star size={12} />
                        </button>
                      )}
                    </div>
                    <div>
                      {primaryExistingImageId !== image.id ? (
                        <input
                          type="number"
                          placeholder="Position"
                          value={existingImagePositions[image.id] || ""}
                          onChange={(e) => handleExistingPositionChange(image.id, e.target.value)}
                          className="w-full text-xs border border-gray-300 rounded px-2 py-1 mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-[10px] text-blue-600 text-center font-medium">
                          Primary (not indexed)
                        </p>
                      )}
                    </div>
                    <ImageSeoFields
                      stacked
                      imageAlt={image.image_alt || ""}
                      imageTitle={image.image_title || ""}
                      onAltChange={(value) =>
                        handleExistingImageSeoChange(index, "image_alt", value)
                      }
                      onTitleChange={(value) =>
                        handleExistingImageSeoChange(index, "image_title", value)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4 pt-8">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload Images <span className="text-lg text-red-500">*</span>
            </label>
            <div className="border border-dashed border-gray-300 p-4 rounded-md relative group">
              <input
                type="file"
                multiple
                onChange={handleImageChange}
                className="hidden"
                id="images"
                accept="image/*"
              />
              <label
                htmlFor="images"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <Upload size={24} className="text-gray-500 mb-2" />
                <span className="text-sm text-gray-500">
                  Click to upload multiple images
                </span>
              </label>
              {/* Tooltip */}
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
                <div className="bg-gray-800 text-white text-xs rounded-md py-2 px-3 whitespace-nowrap shadow-lg">
                  Only the image with 1200*900 resolution can be added
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Display selected images */}
          {formData.images.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Selected Images ({formData.images.length})
              </h4>
              <div className="grid grid-cols-4 gap-4">
                {formData.images.map((file, index) => (
                  <div key={index} className="flex flex-col space-y-2">
                    <div className="relative group">
                      <div className={`h-24 border rounded-md flex items-center justify-center bg-gray-100 overflow-hidden ${index === formData.primaryImageIndex ? 'ring-2 ring-blue-500' : ''}`}>
                        {formData.imagePreviews && formData.imagePreviews[index] ? (
                          <img
                            src={formData.imagePreviews[index]}
                            alt={file.name || `Image ${index + 1}`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <p className="text-xs text-gray-500 p-2 truncate">
                            {file.name || "Image " + (index + 1)}
                          </p>
                        )}
                      </div>

                      {index === formData.primaryImageIndex && (
                        <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full p-1 z-10">
                          <Star size={12} fill="white" />
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-sm"
                      >
                        <Trash2 size={12} />
                      </button>

                      {index !== formData.primaryImageIndex && (
                        <button
                          type="button"
                          onClick={() => setPrimaryImage(index)}
                          className="absolute bottom-1 left-1 bg-green-500 text-white rounded-full p-1 hover:bg-green-600 transition-colors opacity-0 group-hover:opacity-100 z-10 shadow-sm"
                          title="Set as Primary Image"
                        >
                          <Star size={12} />
                        </button>
                      )}
                    </div>
                    <div>
                      {index !== formData.primaryImageIndex ? (
                        <input
                          type="number"
                          placeholder="Position"
                          value={formData.imagePositions[index] || ""}
                          onChange={(e) => handlePositionChange(index, e.target.value)}
                          className="w-full text-xs border border-gray-300 rounded px-2 py-1 mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-[10px] text-blue-600 text-center font-medium">
                          Primary (not indexed)
                        </p>
                      )}
                    </div>
                    <ImageSeoFields
                      stacked
                      imageAlt={formData.imageAlts?.[index] || ""}
                      imageTitle={formData.imageTitles?.[index] || ""}
                      onAltChange={(value) =>
                        handleNewImageSeoChange(index, "image_alt", value)
                      }
                      onTitleChange={(value) =>
                        handleNewImageSeoChange(index, "image_title", value)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-reset-filter justify-center"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`btn justify-center ${isEditing ? "btn-edit text-white" : "btn-primary"
                } ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""}`}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Processing..."
                : isEditing
                  ? "Update"
                  : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommercialInteriorDesignModal;
